import React, { useState, useEffect, useContext } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  TextArea,
  Popup,
  Button,
  Confirm,
} from "semantic-ui-react";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import "../TemplateManager.scss";

export const Sev03 = React.memo((props) => {
  const { index, slideId, onLetterByOrder, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [time, setTime] = useState("");
  const [content, setContent] = useState("");
  const [question, setQuestion] = useState("");
  const [choices, setChoices] = useState([]);

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Synchronizes the component's state with the content of the currently selected slide.
   *
   * This `useEffect` hook is executed whenever the `slideId` changes. It searches the `slidesActiveSessionEdit`
   * array for a slide that matches the `slideId`. If such a slide is found and it contains content, the hook
   * updates the component's state to reflect this content. Specifically, it performs the following actions:
   *
   * - Sets the `time` state with the slide's time value.
   * - Sets the `content` state with the slide's main content.
   * - Sets the `question` state with the slide's question text.
   * - Sets the `choices` state with the slide's array of choices.
   *
   * This ensures that the component accurately represents the content of the current slide, allowing for
   * effective editing and previewing within an active session.
   *
   * @dependency slideId - The ID of the current slide, changes to which trigger the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTime(slide.content.time);
      setContent(slide.content.content);
      setQuestion(slide.content.question);
      setChoices(slide.content.choices);
    }
  }, [slideId]);

  /**
   * Updates the content of a specific slide within the `slidesActiveSessionEdit` array.
   *
   * This `useEffect` hook is triggered whenever there are changes to the `time`, `question`, or `content`
   * states. It begins by finding the index of the slide that matches the `slideId` within the
   * `slidesActiveSessionEdit` array. If the slide is found (indicated by `slideIndex` not being -1),
   * the hook updates the slide's `content` object with the new values for `time`, `question`, and
   * `content`.
   *
   * This ensures that any changes made to the time allocation, question text, or main content of the
   * slide are immediately reflected within the active session's slide array. This is crucial for
   * maintaining the integrity and accuracy of the session content as it is being edited.
   *
   * @dependency time - The current time value to be updated for the slide.
   * @dependency question - The current question content to be updated for the slide.
   * @dependency content - The current main content to be updated for the slide.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.time = time;
      slidesActiveSessionEdit[slideIndex].content.question = question;
      slidesActiveSessionEdit[slideIndex].content.content = content;
    }
  }, [time, question, content]);

  /**
   * Defines event handlers for updating session content in a slide editing component.
   *
   * `handleTimeChange`:
   * - Updates the `time` state based on the input value from the event. This is typically bound to a time input field, allowing the user to set the duration for the slide.
   *
   * `handleContentChange`:
   * - Updates the `content` state, removing any leading whitespace from the input value. This ensures that the content text starts immediately without any leading spaces, maintaining a clean and consistent format.
   *
   * `handleQuestionChange`:
   * - Updates the `question` state in a similar manner to `handleContentChange`, trimming leading whitespace from the input value. This is used for editing the question text associated with the slide.
   *
   * `handleChoiceChange`:
   * - Updates a specific choice's `answer` within the `choices` array. It accepts an `index` to identify which choice to update and the `event` object to access the new value. The choice's answer is updated, with leading whitespace removed, ensuring consistency in the presentation of choices.
   *
   * These handlers facilitate the dynamic updating of slide content, allowing users to edit the time, main content, question, and choices associated with a slide in real-time.
   */
  const handleTimeChange = (event) => {
    setTime(event.target.value);
  };

  const handleContentChange = (event) => {
    setContent(event.target.value.trimStart());
  };

  const handleQuestionChange = (event) => {
    setQuestion(event.target.value.trimStart());
  };

  const handleChoiceChange = (index, event) => {
    const newChoices = [...choices];
    newChoices[index].answer = event.target.value.trimStart();
    setChoices(newChoices);
  };

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__time">
          <div className="media-slide__label-div">
            <Icon name="clock outline" />
            <h5 className="media-slide__label">Duración:</h5>
          </div>
          <Input
            className="media-slide__input-time"
            labelPosition="right"
            type="number"
            min="1"
            name="time"
            value={time}
            onChange={handleTimeChange}
          >
            <input />
            <Label className="media-slide__input-time-label">minuto(s)</Label>
          </Input>
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="book" />
            <h5 className="media-slide__label">Lectura previa:</h5>
          </div>
          <TextArea
            className="media-slide__textarea"
            rows={5}
            name="content"
            value={content}
            onChange={handleContentChange}
          />
          <div className="media-slide__label-div">
            <Icon name="question" />
            <h5 className="media-slide__label">Pregunta:</h5>
          </div>
          <TextArea
            className="media-slide__textarea"
            rows={5}
            name="question"
            value={question}
            onChange={handleQuestionChange}
          />
          <div>
            {choices.map((choice, choiceIndex) => (
              <div className="col-md-12 mb-1" key={choiceIndex}>
                <div className="input-group border">
                  <Input
                    className="media-slide__input-choice"
                    labelPosition="left"
                    value={choice.answer}
                    onChange={(e) => handleChoiceChange(choiceIndex, e)}
                    onClick={(event) => event.target.focus()}
                  >
                    <Label
                      className={`media-slide__input-label${
                        choice.correct ? "-success" : ""
                      }`}
                    >
                      {onLetterByOrder(choiceIndex + 1)}.
                    </Label>
                    <input />
                  </Input>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
